import {
  AddOutlined,
  ArrowDropDownRounded,
  ArrowRightRounded,
  GavelOutlined,
  GroupsOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  KeyboardArrowDownRounded,
  StorefrontOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { amber, grey } from "@mui/material/colors";
import { memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../../../../_src_shared/utils/functions/formatDollar";
import { slate, teal } from "../../../../../theme/colors";
import {
  APPRAISAL_OUTCOME_LABEL,
  APPRAISAL_STATUS_META,
  AppraisalStatusChip,
  orderAppraisalDocOids,
} from "../../../../../components/appraisalOrders/appraisalOrderUi";
import {
  SecondaryStatusChip,
  secondaryStatusMeta,
} from "../../../../../components/appraisalOrders/secondaryOrderUi";
import {
  canCancel,
  canMarkReceived,
  canMarkSubmitted,
  secondaryOrderId,
} from "../../../../../api/secondaryValuationOrdersApi";
import { getUserFullNameFromEmail } from "../../../../../utils/functions/getUserFullNameFromEmail";
import { addressLabel } from "../../../../../_src_shared/utils/functions/addressLabel";
import { SECONDARY_ACTION } from "./SecondaryOrderActionDialogs";

// ObjectIds arrive as plain strings or Mongo extended-JSON ({ $oid }).
const oidStr = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v.$oid || v._id || null;
  return String(v);
};

const propertyLabel = (p) =>
  p?.stringAddressFinalTitle ||
  addressLabel(p?.address) ||
  p?.address?.streetAddress ||
  p?.address?.street ||
  "Subject property";

/* ─────────────────────────────────────────────
   Appraisal Review — a single card that carries the "order" action AND lists
   every subject property on the loan inline. A review is either INTERNAL (the
   Internal Appraisers queue — `orders`) or ordered through an OUTSIDE party
   (a vendor / AMC secondary valuation — `secondaryOrders`). Properties with
   orders show one row per order; a property with no active order shows a
   "Not ordered" row whose Order review menu offers both routes with that
   property pre-selected. Orders whose property no longer exists on the loan
   still render at the bottom so nothing placed is hidden.
   ───────────────────────────────────────────── */

const isActiveInternal = (o) => o?.status !== "cancelled";
const isActiveSecondary = (o) => o?.status !== "cancelled";

const AppraisalOrdersCard = memo(
  ({
    orders = [],
    secondaryOrders = [],
    subjectProperties = [],
    loanDocuments = [],
    onOrder,
    onOrderOutside,
    onView,
    onSecondaryAction,
    onPreviewDoc,
  }) => {
    const users = useSelector((state) => state.users);
    const nameOf = useCallback(
      (email) => (email ? getUserFullNameFromEmail(users, email) || email : null),
      [users],
    );

    const activeCount =
      orders.filter(isActiveInternal).length +
      secondaryOrders.filter(isActiveSecondary).length;

    // Group both kinds of order under their subject property, preserving the
    // loan's property order; anything unmatched goes to `orphans`.
    const { rows, orphans } = useMemo(() => {
      const byProperty = new Map();
      const orphans = [];
      const propertyIds = new Set(
        subjectProperties.map((p) => oidStr(p?._id) || oidStr(p?.oid)),
      );
      const place = (entry) => {
        const pid = oidStr(entry.order?.subjectPropertyId);
        if (pid && propertyIds.has(pid)) {
          if (!byProperty.has(pid)) byProperty.set(pid, []);
          byProperty.get(pid).push(entry);
        } else {
          orphans.push(entry);
        }
      };
      orders.forEach((o) => place({ external: false, order: o }));
      secondaryOrders.forEach((o) => place({ external: true, order: o }));
      const rows = subjectProperties
        .map((p) => {
          const pid = oidStr(p?._id) || oidStr(p?.oid);
          if (!pid) return null;
          const entries = byProperty.get(pid) || [];
          const hasActive = entries.some((e) =>
            e.external ? isActiveSecondary(e.order) : isActiveInternal(e.order),
          );
          return { pid, property: p, entries, hasActive };
        })
        .filter(Boolean);
      return { rows, orphans };
    }, [orders, secondaryOrders, subjectProperties]);

    const hasAnyRow = rows.length > 0 || orphans.length > 0;
    const orderedPropertyCount = rows.filter((r) => r.hasActive).length;
    let rowIndex = 0;

    const renderEntry = (entry, key) =>
      entry.external ? (
        <SecondaryOrderRow
          key={key}
          order={entry.order}
          loanDocuments={loanDocuments}
          divider={rowIndex++ > 0}
          nameOf={nameOf}
          onAction={onSecondaryAction}
          onPreviewDoc={onPreviewDoc}
        />
      ) : (
        <OrderRow
          key={key}
          order={entry.order}
          loanDocuments={loanDocuments}
          divider={rowIndex++ > 0}
          nameOf={nameOf}
          onView={() => onView?.(entry.order)}
          onPreviewDoc={onPreviewDoc}
        />
      );
    const entryKey = (entry, fallback) =>
      `${entry.external ? "ext" : "int"}:${
        (entry.external ? secondaryOrderId(entry.order) : oidStr(entry.order?._id)) ||
        fallback
      }`;

    return (
      <Box
        sx={{
          background: "#fff",
          border: `1px solid ${grey[200]}`,
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        }}
      >
        {/* Header */}
        <Flx
          ac
          jb
          g={1.5}
          sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${grey[100]}` }}
        >
          <Flx ac g={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: amber[50],
                color: amber[800],
                flexShrink: 0,
              }}
            >
              <GavelOutlined sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Flx ac wrap g={1} sx={{ mb: 0.3 }}>
                <Txt
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: grey[900],
                  }}
                >
                  Appraisal Review
                </Txt>
                <Box
                  sx={{
                    fontSize: 10.5,
                    px: 0.9,
                    py: "1px",
                    background: grey[100],
                    borderRadius: "4px",
                    color: grey[700],
                    fontWeight: 500,
                  }}
                >
                  Appraisal
                </Box>
                {activeCount > 0 && (
                  <Box
                    sx={{
                      fontSize: 10.5,
                      px: 0.9,
                      py: "1px",
                      background: amber[100],
                      borderRadius: "4px",
                      color: amber[900],
                      fontWeight: 700,
                    }}
                  >
                    {activeCount} active
                  </Box>
                )}
              </Flx>
              <Txt sx={{ fontSize: 12, color: grey[700], fontWeight: 500 }}>
                Internal Appraisers or an outside party
              </Txt>
            </Box>
          </Flx>

          {/* Coverage summary — ordering happens per property row below. */}
          {rows.length > 0 && (
            <Txt
              sx={{
                fontSize: 12.5,
                color: slate[500],
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: slate[800] }}>
                {orderedPropertyCount}
              </Box>{" "}
              of{" "}
              <Box component="span" sx={{ fontWeight: 700, color: slate[800] }}>
                {rows.length}
              </Box>{" "}
              {rows.length === 1 ? "property" : "properties"} ordered
            </Txt>
          )}
        </Flx>

        {/* Property / order rows */}
        {!hasAnyRow ? (
          <Flx center sx={{ py: 3, px: 2 }}>
            <Txt sx={{ fontSize: 12.5, color: grey[500] }}>
              No subject properties on this loan yet.
            </Txt>
          </Flx>
        ) : (
          <Box>
            {rows.map(({ pid, property, entries, hasActive }) => (
              <Box key={`property:${pid}`}>
                {entries.map((entry, i) =>
                  renderEntry(entry, entryKey(entry, `${pid}:${i}`)),
                )}
                {/* No live order (none yet, or every one cancelled) → offer
                    both ordering routes for this property. */}
                {!hasActive && (
                  <NotOrderedRow
                    property={property}
                    reordering={entries.length > 0}
                    divider={rowIndex++ > 0}
                    onOrderInternal={() => onOrder?.(pid)}
                    onOrderOutside={() => onOrderOutside?.(pid)}
                  />
                )}
              </Box>
            ))}
            {orphans.map((entry, i) =>
              renderEntry(entry, entryKey(entry, `orphan:${i}`)),
            )}
          </Box>
        )}
      </Box>
    );
  },
);

/* ─────────────────────────────────────────────
   Row primitives
   ───────────────────────────────────────────── */

// Left accent bar + row padding shared by both row types. The bar color is the
// status's dot color so a glance down the card reads the pipeline.
const rowShellSx = (accent, divider) => ({
  position: "relative",
  px: 2,
  py: 2.5,
  pl: "20px",
  ...(divider ? { borderTop: `1px solid ${grey[100]}` } : {}),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: accent,
  },
});

// The row-level action buttons (Order review / View order) share one size so
// the right edge of the card lines up.
const rowButtonSx = {
  textTransform: "none",
  whiteSpace: "nowrap",
  fontSize: 13,
  fontWeight: 600,
  px: 1.8,
  py: 1.4,
  minWidth: 0,
  flexShrink: 0,
  lineHeight: 1.4,
};

const addressSx = {
  fontSize: 13.5,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  color: grey[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

// Small neutral pill (the "Due Sep 30 · in 19 days" tag).
const Pill = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: 11,
      fontWeight: 500,
      color: slate[600],
      background: slate[100],
      borderRadius: "6px",
      px: "8px",
      py: "2px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

// "in 19 days" / "today" / "3 days overdue" for a DATE-ONLY value. Compares
// calendar days (UTC parts of the value vs. the viewer's local calendar day)
// so a negative-offset zone can't slip the count.
const relativeDays = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff > 1) return `in ${diff} days`;
  if (diff === -1) return "1 day overdue";
  return `${Math.abs(diff)} days overdue`;
};

// Three-step progress strip: Queued → In review → Complete. `assigned` still
// sits on the first step (the appraiser has claimed it but not started).
const PROGRESS_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "in_review", label: "In review" },
  { key: "completed", label: "Complete" },
];
const stepIndexFor = (status) => {
  if (status === "completed") return 2;
  if (status === "in_review") return 1;
  return 0; // queued / assigned
};

// "● Queued ——— ○ In review ——— ○ Complete": only the CURRENT step is colored
// (status dot + text); every other dot, label and connector is neutral grey.
const ProgressStrip = ({ status }) => {
  const current = stepIndexFor(status);
  const meta = APPRAISAL_STATUS_META[status] || APPRAISAL_STATUS_META.queued;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "8px",
      }}
    >
      {PROGRESS_STEPS.map((step, i) => {
        const isCurrent = i === current;
        return (
          <Box
            key={step.key}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {i > 0 && (
              <Box
                sx={{
                  width: "24px",
                  height: "1px",
                  background: slate[300],
                  flexShrink: 0,
                }}
              />
            )}
            <Box
              sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: isCurrent ? meta.dot : slate[300],
                  flexShrink: 0,
                }}
              />
              <Txt
                sx={{
                  fontSize: 12,
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent ? meta.fg : slate[400],
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                }}
              >
                {step.label}
              </Txt>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|heic|bmp|tiff?)$/i;
const docLabel = (doc) =>
  doc?.file_display_name || doc?.file_name_as_loaded || "Document not on loan";
const DocIcon = ({ doc }) =>
  IMAGE_EXT.test(docLabel(doc)) ? (
    <ImageOutlined sx={{ fontSize: 14 }} />
  ) : (
    <InsertDriveFileOutlined sx={{ fontSize: 14 }} />
  );

// A subject property with no live review: neutral accent, "Not ordered" chip
// beside the address, and an Order review button whose menu picks the route —
// the Internal Appraisers, or an outside party (vendor / AMC). Either opens its
// sidebar pre-targeted at this property.
const NotOrderedRow = memo(
  ({ property, reordering, divider, onOrderInternal, onOrderOutside }) => {
    const [anchor, setAnchor] = useState(null);
    const pick = (fn) => () => {
      setAnchor(null);
      fn?.();
    };
    return (
      <Flx ac jb g={2} sx={rowShellSx(slate[300], divider)}>
        <Box sx={{ minWidth: 0 }}>
          <Flx ac g={1} sx={{ minWidth: 0 }}>
            <Txt component="div" sx={addressSx}>
              {propertyLabel(property)}
            </Txt>
            <AppraisalStatusChip status="not_ordered" />
          </Flx>
          <Txt component="div" sx={{ fontSize: 12, color: slate[500], mt: 0.4 }}>
            {reordering
              ? "Every review ordered for this property was cancelled."
              : "No review has been ordered for this property yet."}
          </Txt>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddOutlined sx={{ fontSize: 17 }} />}
          endIcon={<KeyboardArrowDownRounded sx={{ fontSize: 18 }} />}
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          sx={rowButtonSx}
        >
          Order review
        </Button>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { mt: 0.5, minWidth: 300 } } }}
        >
          <MenuItem onClick={pick(onOrderInternal)} sx={{ py: 1.25 }}>
            <ListItemIcon>
              <GroupsOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Internal appraisers"
              secondary="Our valuation team reviews the appraisal."
              primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
          <MenuItem onClick={pick(onOrderOutside)} sx={{ py: 1.25 }}>
            <ListItemIcon>
              <StorefrontOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Outside party"
              secondary="Ordered through a vendor or AMC."
              primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        </Menu>
      </Flx>
    );
  },
);

// One placed order: address + status + due pill, a progress strip with a
// details toggle, and (expanded) the instructions / documents panel + an
// ordered-by footer.
const OrderRow = memo(
  ({ order, loanDocuments = [], divider, nameOf, onView, onPreviewDoc }) => {
    const [open, setOpen] = useState(false);
    const status = order?.status;
    const cancelled = status === "cancelled";
    const meta = APPRAISAL_STATUS_META[status] || APPRAISAL_STATUS_META.queued;

    const dueShort = order?.requestedByDate
      ? formatDate(order.requestedByDate, "", "MMM d")
      : null;
    const dueLong = order?.requestedByDate
      ? formatDate(order.requestedByDate, "", "MMM d, yyyy")
      : null;
    const dueRel = relativeDays(order?.requestedByDate);
    const ordered = order?.createdAt
      ? formatDateTime(order.createdAt, "MMM d, yyyy")
      : null;
    const outcome =
      status === "completed" && order?.reviewOutcome
        ? APPRAISAL_OUTCOME_LABEL[order.reviewOutcome] || order.reviewOutcome
        : null;
    const instructions =
      typeof order?.instructions === "string" ? order.instructions.trim() : "";

    // One chip per appraisal on the order, resolved against the loan's docs
    // so the chip can open the global file preview. An id with no matching
    // doc (deleted since) still renders, disabled, so the operator sees it.
    const attachedDocs = useMemo(() => {
      const oids = orderAppraisalDocOids(order);
      return oids.map((oid) => ({
        oid,
        doc: loanDocuments.find((d) => oidStr(d?._id) === oid) || null,
      }));
    }, [order, loanDocuments]);

    const footer = [
      order?.requestedBy
        ? ["Ordered by", nameOf?.(order.requestedBy) || order.requestedBy]
        : null,
      ordered ? ["Ordered", ordered] : null,
      dueLong ? ["Requested-by date", dueLong] : null,
      order?.assignedTo
        ? ["Assigned to", nameOf?.(order.assignedTo) || order.assignedTo]
        : null,
      outcome ? ["Outcome", outcome] : null,
    ].filter(Boolean);

    return (
      <Box sx={rowShellSx(meta.dot, divider)}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Flx column g={1} sx={{ minWidth: 0, flex: 1 }}>
            {/* Line 1 — address · status · due */}
            <Flx ac wrap g={1} sx={{ minWidth: 0 }}>
              <Txt component="div" sx={addressSx}>
                {order?.subjectPropertyAddress || "Subject property"}
              </Txt>
              <AppraisalStatusChip status={status} />
              <KindTag icon={GroupsOutlined}>Internal appraisers</KindTag>
              {dueShort && (
                <Pill>
                  Due {dueShort}
                  {dueRel && !cancelled ? ` · ${dueRel}` : ""}
                </Pill>
              )}
            </Flx>

            {/* Line 2 — progress · details toggle. The toggle is a bare text
                link (not a MUI Button) so it sits on the same baseline as the
                progress labels with no button padding around it. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {!cancelled && <ProgressStrip status={status} />}
              {!cancelled && (
                <Box
                  sx={{
                    width: "1px",
                    height: "14px",
                    background: slate[300],
                    flexShrink: 0,
                  }}
                />
              )}
              <Box
                component="button"
                type="button"
                onClick={() => setOpen((v) => !v)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  p: 0,
                  m: 0,
                  border: "none",
                  background: "none",
                  font: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: teal[600],
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { color: teal[700], textDecoration: "underline" },
                }}
              >
                {open ? (
                  <ArrowDropDownRounded sx={{ fontSize: 18, ml: "-3px" }} />
                ) : (
                  <ArrowRightRounded sx={{ fontSize: 18, ml: "-3px" }} />
                )}
                {open ? "Hide order details" : "Show order details"}
              </Box>
            </Box>
          </Flx>

          <Button variant="outlined" onClick={onView} sx={rowButtonSx}>
            View order
          </Button>
        </Flx>

        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                background: slate[50],
                border: `1px solid ${slate[200]}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Flx
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  "& > *": { flex: 1, minWidth: 0, p: "12px 14px" },
                  "& > * + *": {
                    borderTop: { xs: `1px solid ${slate[200]}`, md: "none" },
                    borderLeft: { md: `1px solid ${slate[200]}` },
                  },
                }}
              >
                <Box>
                  <PanelLabel>Instructions</PanelLabel>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12.5,
                      color: instructions ? slate[700] : slate[400],
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {instructions || "No instructions were provided."}
                  </Txt>
                </Box>
                <Box>
                  <PanelLabel>
                    Documents sent for review ({attachedDocs.length})
                  </PanelLabel>
                  {attachedDocs.length === 0 ? (
                    <Txt sx={{ fontSize: 12.5, color: slate[400] }}>—</Txt>
                  ) : (
                    <Flx ac wrap g={0.75}>
                      {attachedDocs.map(({ oid, doc }) => (
                        <Chip
                          key={oid}
                          icon={<DocIcon doc={doc} />}
                          label={docLabel(doc)}
                          disabled={!doc}
                          // Every displayed file is clickable + previewable.
                          onClick={
                            doc && onPreviewDoc
                              ? (e) => {
                                  e.stopPropagation();
                                  onPreviewDoc(doc);
                                }
                              : undefined
                          }
                          sx={{
                            height: 30,
                            fontSize: 12.5,
                            fontWeight: 500,
                            maxWidth: 320,
                            background: "#fff",
                            border: `1px solid ${slate[200]}`,
                            color: slate[700],
                            paddingLeft: "10px",
                            paddingRight: "14px",
                            "& .MuiChip-label": {},
                            "& .MuiChip-icon": {
                              color: slate[500],
                              marginLeft: "12px",
                              marginRight: 0,
                            },
                            "&:hover": doc ? { background: slate[100] } : {},
                          }}
                        />
                      ))}
                    </Flx>
                  )}
                </Box>
              </Flx>

              {footer.length > 0 && (
                <Flx
                  ac
                  wrap
                  g={2.5}
                  sx={{
                    px: "14px",
                    py: "8px",
                    borderTop: `1px solid ${slate[200]}`,
                    background: "#fff",
                  }}
                >
                  {footer.map(([k, v]) => (
                    <Txt key={k} sx={{ fontSize: 11.5, color: slate[500] }}>
                      {k}{" "}
                      <Box
                        component="span"
                        sx={{ color: slate[700], fontWeight: 500 }}
                      >
                        {v}
                      </Box>
                    </Txt>
                  ))}
                </Flx>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  },
);

// Which route a review took — "Internal appraisers" vs "Outside party · <vendor>".
const KindTag = ({ icon: Icon, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      fontSize: 11,
      fontWeight: 500,
      color: slate[600],
      border: `1px solid ${slate[200]}`,
      borderRadius: "6px",
      px: "7px",
      py: "1px",
      whiteSpace: "nowrap",
      maxWidth: 260,
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {Icon ? <Icon sx={{ fontSize: 13, color: slate[500] }} /> : null}
    {children}
  </Box>
);

// A loan document chip that opens the file preview (every displayed file is
// clickable). An id with no matching doc (deleted since) renders disabled.
const DocChip = ({ doc, onPreviewDoc }) => (
  <Chip
    icon={<DocIcon doc={doc} />}
    label={docLabel(doc)}
    disabled={!doc}
    onClick={
      doc && onPreviewDoc
        ? (e) => {
            e.stopPropagation();
            onPreviewDoc(doc);
          }
        : undefined
    }
    sx={{
      height: 30,
      fontSize: 12.5,
      fontWeight: 500,
      maxWidth: 320,
      background: "#fff",
      border: `1px solid ${slate[200]}`,
      color: slate[700],
      paddingLeft: "10px",
      paddingRight: "14px",
      "& .MuiChip-icon": {
        color: slate[500],
        marginLeft: "12px",
        marginRight: 0,
      },
      "&:hover": doc ? { background: slate[100] } : {},
    }}
  />
);

// The latest history event that moved the order to `status`.
const lastEventFor = (order, status) =>
  [...(order?.history || [])].reverse().find((e) => e?.status === status) ||
  null;

// One outside-party (vendor / AMC) secondary valuation: address · status ·
// vendor · due, who ordered it and when, the actions its status allows
// (Mark placed → Mark received; Cancel in the details), and — expanded — the
// instructions, the primary appraisal(s), and the received values.
const SecondaryOrderRow = memo(
  ({ order, loanDocuments = [], divider, nameOf, onAction, onPreviewDoc }) => {
    const [open, setOpen] = useState(false);
    const status = order?.status;
    const cancelled = status === "cancelled";
    const meta = secondaryStatusMeta(status);

    const findDoc = (oid) =>
      oid ? loanDocuments.find((d) => oidStr(d?._id) === String(oid)) || null : null;

    const dueShort = order?.requestedByDate
      ? formatDate(order.requestedByDate, "", "MMM d")
      : null;
    const dueLong = order?.requestedByDate
      ? formatDate(order.requestedByDate, "", "MMM d, yyyy")
      : null;
    const dueRel =
      status === "requested" || status === "submitted"
        ? relativeDays(order?.requestedByDate)
        : null;
    const ordered = order?.createdAt
      ? formatDateTime(order.createdAt, "MMM d, yyyy")
      : null;
    const orderedBy = order?.requestedBy ? nameOf?.(order.requestedBy) : null;
    const instructions =
      typeof order?.instructions === "string" ? order.instructions.trim() : "";
    const primaryDocs = (order?.primaryAppraisalDocOids || []).map((oid) => ({
      oid: String(oid),
      doc: findDoc(oid),
    }));
    const received = order?.received || null;
    const receivedEvent = lastEventFor(order, "received");
    const receivedDoc = findDoc(received?.documentOid);
    const receivedValues = received
      ? [
          received.asIsValue != null
            ? ["As-is value", formatDollar(received.asIsValue, "—")]
            : null,
          received.arv != null
            ? ["After-repair value", formatDollar(received.arv, "—")]
            : null,
          received.effectiveDate
            ? ["Effective date", formatDate(received.effectiveDate, "", "MMM d, yyyy")]
            : null,
        ].filter(Boolean)
      : [];

    const footer = [
      orderedBy ? ["Ordered by", orderedBy] : null,
      ordered ? ["Ordered", ordered] : null,
      dueLong ? ["Needed by", dueLong] : null,
      order?.providerOrderId ? ["Vendor order #", order.providerOrderId] : null,
      receivedEvent?.at
        ? [
            "Received",
            `${formatDateTime(receivedEvent.at, "MMM d, yyyy")}${
              receivedEvent.by ? ` by ${nameOf?.(receivedEvent.by)}` : ""
            }`,
          ]
        : null,
      cancelled && order?.cancelReason ? ["Cancel reason", order.cancelReason] : null,
    ].filter(Boolean);

    const act = (type) => () => onAction?.(type, order);

    return (
      <Box sx={rowShellSx(meta.tone.solid, divider)}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Flx column g={1} sx={{ minWidth: 0, flex: 1 }}>
            {/* Line 1 — address · status · route/vendor · due */}
            <Flx ac wrap g={1} sx={{ minWidth: 0 }}>
              <Txt component="div" sx={addressSx}>
                {order?.subjectPropertyAddress || "Subject property"}
              </Txt>
              <SecondaryStatusChip status={status} />
              <KindTag icon={StorefrontOutlined}>
                Outside party{order?.provider ? ` · ${order.provider}` : ""}
              </KindTag>
              {dueShort && (
                <Pill>
                  Due {dueShort}
                  {dueRel ? ` · ${dueRel}` : ""}
                </Pill>
              )}
            </Flx>

            {/* Line 2 — who ordered it · details toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <Txt sx={{ fontSize: 12, color: slate[500] }}>
                {orderedBy ? (
                  <>
                    Ordered by{" "}
                    <Box component="span" sx={{ color: slate[700], fontWeight: 600 }}>
                      {orderedBy}
                    </Box>
                  </>
                ) : (
                  "Ordered"
                )}
                {ordered ? ` · ${ordered}` : ""}
                {status === "requested"
                  ? " · waiting for the vendor's order number"
                  : ""}
              </Txt>
              <Box
                sx={{
                  width: "1px",
                  height: "14px",
                  background: slate[300],
                  flexShrink: 0,
                }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setOpen((v) => !v)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  p: 0,
                  m: 0,
                  border: "none",
                  background: "none",
                  font: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: teal[600],
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { color: teal[700], textDecoration: "underline" },
                }}
              >
                {open ? (
                  <ArrowDropDownRounded sx={{ fontSize: 18, ml: "-3px" }} />
                ) : (
                  <ArrowRightRounded sx={{ fontSize: 18, ml: "-3px" }} />
                )}
                {open ? "Hide order details" : "Show order details"}
              </Box>
            </Box>
          </Flx>

          {(canMarkSubmitted(order) || canMarkReceived(order)) && (
            <Flx ac g={1} sx={{ flexShrink: 0 }}>
              {canMarkSubmitted(order) && (
                <Button
                  variant="outlined"
                  onClick={act(SECONDARY_ACTION.SUBMITTED)}
                  sx={rowButtonSx}
                >
                  Mark placed
                </Button>
              )}
              {canMarkReceived(order) && (
                <Button
                  variant="contained"
                  onClick={act(SECONDARY_ACTION.RECEIVED)}
                  sx={rowButtonSx}
                >
                  Mark received
                </Button>
              )}
            </Flx>
          )}
        </Flx>

        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                background: slate[50],
                border: `1px solid ${slate[200]}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Flx
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  "& > *": { flex: 1, minWidth: 0, p: "12px 14px" },
                  "& > * + *": {
                    borderTop: { xs: `1px solid ${slate[200]}`, md: "none" },
                    borderLeft: { md: `1px solid ${slate[200]}` },
                  },
                }}
              >
                <Box>
                  <PanelLabel>Instructions</PanelLabel>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 12.5,
                      color: instructions ? slate[700] : slate[400],
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {instructions || "No instructions were provided."}
                  </Txt>
                </Box>
                <Box>
                  <PanelLabel>Primary appraisal ({primaryDocs.length})</PanelLabel>
                  {primaryDocs.length === 0 ? (
                    <Txt sx={{ fontSize: 12.5, color: slate[400] }}>—</Txt>
                  ) : (
                    <Flx ac wrap g={0.75}>
                      {primaryDocs.map(({ oid, doc }) => (
                        <DocChip key={oid} doc={doc} onPreviewDoc={onPreviewDoc} />
                      ))}
                    </Flx>
                  )}
                </Box>
                {received && (
                  <Box>
                    <PanelLabel>Received</PanelLabel>
                    <Flx column g={0.75}>
                      {receivedValues.length === 0 && !receivedDoc ? (
                        <Txt sx={{ fontSize: 12.5, color: slate[400] }}>
                          No values were entered.
                        </Txt>
                      ) : null}
                      {receivedValues.map(([k, v]) => (
                        <Txt key={k} sx={{ fontSize: 12.5, color: slate[500] }}>
                          {k}{" "}
                          <Box
                            component="span"
                            sx={{ color: slate[800], fontWeight: 600 }}
                          >
                            {v}
                          </Box>
                        </Txt>
                      ))}
                      {receivedDoc && (
                        <Box>
                          <DocChip doc={receivedDoc} onPreviewDoc={onPreviewDoc} />
                        </Box>
                      )}
                      {receivedEvent?.note ? (
                        <Txt
                          component="div"
                          sx={{
                            fontSize: 12.5,
                            color: slate[700],
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {receivedEvent.note}
                        </Txt>
                      ) : null}
                    </Flx>
                  </Box>
                )}
              </Flx>

              {(footer.length > 0 || canCancel(order)) && (
                <Flx
                  ac
                  jb
                  wrap
                  g={2}
                  sx={{
                    px: "14px",
                    py: "8px",
                    borderTop: `1px solid ${slate[200]}`,
                    background: "#fff",
                  }}
                >
                  <Flx ac wrap g={2.5}>
                    {footer.map(([k, v]) => (
                      <Txt key={k} sx={{ fontSize: 11.5, color: slate[500] }}>
                        {k}{" "}
                        <Box
                          component="span"
                          sx={{ color: slate[700], fontWeight: 500 }}
                        >
                          {v}
                        </Box>
                      </Txt>
                    ))}
                  </Flx>
                  {canCancel(order) && (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={act(SECONDARY_ACTION.CANCEL)}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Cancel order
                    </Button>
                  )}
                </Flx>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  },
);

const PanelLabel = ({ children }) => (
  <Txt
    component="div"
    sx={{ fontSize: 11, fontWeight: 600, color: slate[500], mb: 0.75 }}
  >
    {children}
  </Txt>
);

AppraisalOrdersCard.displayName = "AppraisalOrdersCard";
OrderRow.displayName = "AppraisalOrderRow";
NotOrderedRow.displayName = "AppraisalNotOrderedRow";
SecondaryOrderRow.displayName = "AppraisalSecondaryOrderRow";

export default AppraisalOrdersCard;
